import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight } from "lucide-react";

const CATEGORIES = ["Health Insurance", "Life Insurance", "Motor Insurance", "Travel Insurance", "Fire Insurance", "Marine Insurance", "Property Insurance", "Personal Accident", "Commercial Insurance", "Other"];

export default function NewCase() {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", category: "Health Insurance" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/cases", form);
      toast.success(`Case ${data.case_no} created`);
      nav(`/dashboard/cases/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-brand font-semibold">Step 1 of 2</div>
        <h1 className="font-heading font-bold text-4xl mt-2 leading-tight">Register a new case</h1>
        <p className="text-sm text-slate-600 mt-2">A short title and a brief description is all we need to begin. You can upload documents on the next step.</p>

        <form onSubmit={submit} className="mt-8 brand-card p-8 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Case title</Label>
            <Input
              required
              data-testid="new-case-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Health claim — Apollo, March 2026"
              className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0 h-11"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Insurance line</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="new-case-category-select" className="mt-1.5 rounded-xl border-slate-200 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Description</Label>
            <Textarea
              rows={5}
              data-testid="new-case-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Insurer name, policy number, dates, what happened, and what you need help with."
              className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading} data-testid="new-case-submit-btn" className="btn-emerald text-sm">
              {loading ? "Creating…" : "Create case"} <ArrowRight className="w-4 h-4" />
            </button>
            <button type="button" data-testid="new-case-cancel-btn" onClick={() => nav(-1)} className="btn-outline text-sm">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Shell>
  );
}
